import React, { useEffect, useId } from 'react';
import './ConfirmModal.css';

interface ConfirmModalProps {
  title: string;
  message: string;
  onClose?: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  isConfirming?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ 
  title, 
  message, 
  onClose, 
  onConfirm, 
  onCancel,
  confirmText = 'Confirmă',
  cancelText = 'Anulează',
  destructive = false,
  isConfirming = false,
}) => {
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isConfirming) {
        onCancel();
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isConfirming, onCancel, onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isConfirming) {
      onCancel();
      onClose?.();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose?.();
  };

  const handleCancel = () => {
    onCancel();
    onClose?.();
  };

  return (
    <div className="confirm-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="confirm-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
      >
        <div className="confirm-modal-bg"></div>

        <h2 id={titleId} className="confirm-modal-title">{title}</h2>
        <p id={messageId} className="confirm-modal-message">{message}</p>

        <div className="confirm-modal-actions">
          <button type="button" className="confirm-modal-cancel" onClick={handleCancel} disabled={isConfirming}>
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirm-modal-confirm${destructive ? ' destructive' : ''}`}
            onClick={handleConfirm}
            disabled={isConfirming}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
